import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {CategoryCard} from './category-card'
import {AlertTriangleIcon, CalendarIcon, GoogleIcon} from '@components/ui/icon'
import {useState} from 'react'
import {ConnectCalendarsModal} from '@/features/settings/components/connect-calendars-modal'
import {useMe} from '@features/user/queries/useMe'
import {raise} from '@utilities/helpers'
import {Separator} from '@components/ui/separator'
import {routes} from '@utilities/constants/routes'
import {useGoogleLogout} from '@features/settings/queries/useGoogleLogout'
import {Spinner} from '@components/ui/spinner'
import {ButtonLink} from '@components/commons/button-link'
import {Button} from '@components/ui/button'
import {SETTINGS_SECTION_WRAPPER_CLASSNAME} from '@features/settings/constants.ts'

export const Calendar = () => {
    const {t} = useTranslation()
    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data ?? raise('user is nullish')
    const [isConnectCalendarsModalOpen, setIsConnectCalendarsModalOpen] = useState(false)
    const disconnectGoogleCalendarMutation = useGoogleLogout()
    const isConnectedToGoogle = user.calendar?.isConnectedToGoogle
    const isGoogleConnectionExpired = user.calendar?.isGoogleConnectionExpired
    const iCalLink = user.calendar?.icsUrl ?? ''

    const disconnectGoogleCalendar = () => {
        disconnectGoogleCalendarMutation.mutate({payload: {photographerId: user.id}})
    }

    return (
        <div id={'calendar-section'} className={SETTINGS_SECTION_WRAPPER_CLASSNAME}>
            <CategoryCard
                icon={CalendarIcon}
                title={t('settings:personal_information:calendar_label')}
                description={t('settings:personal_information:calendar_description')}
            />
            <div className="flex flex-col gap-6 items-start">
                {isConnectedToGoogle ? (
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex w-full flex-col gap-2.5">
                            <GoogleIcon />

                            <div className="flex flex-col gap-1">
                                <p className="text-neutral-700">
                                    <Trans
                                        i18nKey="settings:personal_information:connected_to"
                                        components={{strong: <strong />}}
                                        values={{email: user.calendar?.googleEmail}}
                                    />
                                </p>
                                {isGoogleConnectionExpired ? (
                                    <div className="flex items-center gap-1.5 text-sm text-danger-600">
                                        <AlertTriangleIcon />
                                        {t('settings:personal_information:expired_error')}
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2 items-start">
                                        <p className="text-sm text-neutral-700">
                                            {t('settings:personal_information:airbnb_calendar')}
                                        </p>
                                        <Button
                                            onClick={disconnectGoogleCalendar}
                                            variant="tertiary"
                                            shape="text"
                                            disabled={
                                                disconnectGoogleCalendarMutation.isPending || userMeQuery.isFetching
                                            }
                                        >
                                            {t('settings:personal_information:disconnect')}
                                            {(disconnectGoogleCalendarMutation.isPending || userMeQuery.isFetching) && (
                                                <Spinner />
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {isGoogleConnectionExpired && (
                                <div className="flex items-center gap-3">
                                    <ButtonLink
                                        variant="secondary"
                                        to={routes.GOOGLE_AUTH_CALLBACK.handle.fullPath}
                                        target="_blank"
                                        disabled={disconnectGoogleCalendarMutation.isPending || userMeQuery.isFetching}
                                    >
                                        {t('settings:personal_information:connect_again')}
                                    </ButtonLink>
                                    <Button
                                        onClick={disconnectGoogleCalendar}
                                        variant="tertiary"
                                        shape="text"
                                        disabled={disconnectGoogleCalendarMutation.isPending || userMeQuery.isFetching}
                                    >
                                        {t('settings:personal_information:disconnect')}
                                        {(disconnectGoogleCalendarMutation.isPending || userMeQuery.isFetching) && (
                                            <Spinner />
                                        )}
                                    </Button>
                                </div>
                            )}

                            <Separator orientation="horizontal" />
                        </div>
                    </div>
                ) : (
                    <ButtonLink variant="secondary" to={routes.GOOGLE_AUTH_CALLBACK.handle.fullPath} target="_blank">
                        <GoogleIcon />
                        {t('settings:personal_information:connect_google_calendar')}
                    </ButtonLink>
                )}
                <div className="flex flex-col gap-1 text-sm [&_button]:cursor-pointer [&_button]:underline [&_button:disabled]:cursor-not-allowed [&_button:disabled]:opacity-50 [&_h3]:text-md [&_h3]:font-medium">
                    <h3>{t('settings:personal_information:another_calendar_label')}</h3>
                    <p>
                        <Trans
                            i18nKey="settings:personal_information:another_calendar_text"
                            components={{
                                button: <button onClick={() => setIsConnectCalendarsModalOpen(true)} />
                            }}
                        />
                    </p>
                </div>
            </div>

            {isConnectCalendarsModalOpen && (
                <ConnectCalendarsModal
                    onClose={() => setIsConnectCalendarsModalOpen(false)}
                    iCalLink={iCalLink}
                    userId={user.id}
                />
            )}
        </div>
    )
}
